import { useState } from "react";
import { EditUserAdminForm, ProfileUserData } from "../models/adminUsers.model";
import { editUserAdminService } from "../services/adminUser.service";
import { ToastControlBackendModel } from "../../../../../components/Sidebar/ToastBackControl/models/toastBackControl.model";

interface Props {
  getAllUsersData: () => void;
  setDialogEditUser: (data: boolean) => void;
  dataUser: ProfileUserData;
  setMessageBackend: (data: ToastControlBackendModel) => void;
}

const roles = [
  { name: "Administrador", code: "admin" },
  { name: "Usuario", code: "user" },
  { name: "Moderador", code: "moderator" },
];

function FormEditUser({
  getAllUsersData,
  setDialogEditUser,
  dataUser,
  setMessageBackend,
}: Props): JSX.Element {
  const [user, setUser] = useState<string>(dataUser.user);
  const [name, setName] = useState<string>(dataUser.name);
  const [lastName, setLastName] = useState<string>(dataUser.lastName);
  const [phone, setPhone] = useState<string>(dataUser.phone);
  const [role, setRole] = useState<string>(dataUser.role);
  const [state, setState] = useState<boolean>(dataUser.state);
  const [balance, setBalance] = useState<number>(dataUser.balance);
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data: EditUserAdminForm = {
        idUser: dataUser.idUser,
        user,
        name,
        lastName,
        phone,
        role,
        state,
        balance,
      };
      const response = await editUserAdminService(data);
      if (response) {
        getAllUsersData();
        setDialogEditUser(false);
        setMessageBackend({
          detailValue: "Usuario editado con éxito",
          severityValue: "success",
          lifeValue: 3000,
          validateShowMessage: true,
        });
      }
    } catch {
      setMessageBackend({
        detailValue: "Ocurrió un error al editar el usuario",
        severityValue: "error",
        lifeValue: 3000,
        validateShowMessage: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Shared styles ──────────────────────────────────────────────────────────
  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "5px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#6b7280",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "9px 12px",
    fontSize: "14px",
    color: "#374151",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
    boxSizing: "border-box",
  };

  const fieldStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <form
      onSubmit={onSubmit}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
      }}
    >
      {/* Usuario */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Usuario *</label>
        <input
          autoFocus
          maxLength={100}
          minLength={2}
          type="text"
          placeholder="Ej. juan.perez"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#9ca3af";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(156,163,175,0.2)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e7eb";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Nombre */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Nombre *</label>
        <input
          maxLength={100}
          minLength={2}
          type="text"
          placeholder="Ej. Juan"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#9ca3af";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(156,163,175,0.2)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e7eb";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Apellido */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Apellido *</label>
        <input
          maxLength={100}
          minLength={2}
          type="text"
          placeholder="Ej. Pérez"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#9ca3af";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(156,163,175,0.2)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e7eb";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Teléfono */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Teléfono *</label>
        <input
          maxLength={20}
          minLength={2}
          type="text"
          placeholder="Ej. +57 300 000 0000"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#9ca3af";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(156,163,175,0.2)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e7eb";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Rol */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Rol del Sistema *</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#9ca3af";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(156,163,175,0.2)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e7eb";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <option value="" disabled>Seleccione un rol</option>
          {roles.map((r) => (
            <option key={r.code} value={r.code}>{r.name}</option>
          ))}
        </select>
      </div>

      {/* Saldo */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Saldo</label>
        <input
          type="number"
          placeholder="0.00"
          value={balance}
          onChange={(e) => setBalance(Number(e.target.value))}
          style={inputStyle}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#9ca3af";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(156,163,175,0.2)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e7eb";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Estado del Usuario — full width */}
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: "10px",
          padding: "12px 16px",
        }}
      >
        <div>
          <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "#374151" }}>
            Estado del Usuario
          </p>
          <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#9ca3af" }}>
            Determina si el usuario esta  activo o inactivo en el sistema.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: "20px",
              backgroundColor: state ? "#dcfce7" : "#fee2e2",
              color: state ? "#16a34a" : "#dc2626",
            }}
          >
            {state ? "Activo" : "Inactivo"}
          </span>

          {/* Toggle switch sin dependencias */}
          <button
            type="button"
            role="switch"
            aria-checked={state}
            onClick={() => setState(!state)}
            style={{
              position: "relative",
              width: "44px",
              height: "24px",
              borderRadius: "12px",
              border: "none",
              cursor: "pointer",
              backgroundColor: state ? "#00A86B" : "#d1d5db",
              transition: "background-color 0.2s",
              padding: 0,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: "absolute",
                top: "3px",
                left: state ? "23px" : "3px",
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </button>
        </div>
      </div>

      {/* Botones — full width */}
      <div
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginTop: "4px",
        }}
      >
        <button
          type="button"
          onClick={() => setDialogEditUser(false)}
          style={{
            padding: "9px 18px",
            fontSize: "13px",
            fontWeight: 600,
            color: "#6b7280",
            backgroundColor: "transparent",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "background-color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        >
          Cancelar
        </button>

        <button
          type="submit"
          disabled={loading}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "9px 20px",
            fontSize: "13px",
            fontWeight: 600,
            color: loading ? "#9ca3af" : "#ffffff",
            backgroundColor: loading ? "#e5e7eb" : "#00A86B",
            border: "none",
            borderRadius: "8px",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "opacity 0.15s",
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={(e) => { if (!loading) e.currentTarget.style.opacity = "1"; }}
        >
          {/* Checkmark icon */}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 7.5L5.5 11L12 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {loading ? "Guardando..." : "Guardar Cambios"}
        </button>
      </div>
    </form>
  );
}

export default FormEditUser;